import { memo, useCallback, useMemo, useState } from 'react';

import { Icon } from './Icon';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

export interface TransferListItem {
    id: string;
    label: string;
}

export interface TransferListProps {
    left: TransferListItem[];
    right: TransferListItem[];
    onChange: (left: TransferListItem[], right: TransferListItem[]) => void;
    leftTitle?: string;
    rightTitle?: string;
    isLoading?: boolean;
}

interface TransferListRowProps {
    item: TransferListItem;
    checked: boolean;
    disabled: boolean;
    onToggle: (id: string) => void;
}

const TransferListRow = memo(function TransferListRow({
    item,
    checked,
    disabled,
    onToggle,
}: TransferListRowProps) {
    return (
        <ListItemButton
            role="listitem"
            onClick={() => onToggle(item.id)}
            disabled={disabled}
        >
            <ListItemIcon>
                <Checkbox
                    checked={checked}
                    tabIndex={-1}
                    disableRipple
                    disabled={disabled}
                    aria-labelledby={`transfer-list-item-${item.id}-label`}
                />
            </ListItemIcon>
            <ListItemText
                id={`transfer-list-item-${item.id}-label`}
                primary={item.label}
            />
        </ListItemButton>
    );
});

function not(a: TransferListItem[], b: Set<string>): TransferListItem[] {
    return a.filter((item) => !b.has(item.id));
}

export const TransferList = ({
    left,
    right,
    onChange,
    leftTitle = 'Available',
    rightTitle = 'Selected',
    isLoading = false,
}: TransferListProps) => {
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    const filteredLeft = useMemo(
        () =>
            searchQuery
                ? left.filter((item) =>
                      item.label
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                  )
                : left,
        [left, searchQuery],
    );

    const filteredRight = useMemo(
        () =>
            searchQuery
                ? right.filter((item) =>
                      item.label
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                  )
                : right,
        [right, searchQuery],
    );

    const leftChecked = useMemo(
        () => left.filter((item) => checkedIds.has(item.id)),
        [left, checkedIds],
    );
    const rightChecked = useMemo(
        () => right.filter((item) => checkedIds.has(item.id)),
        [right, checkedIds],
    );

    const handleToggle = useCallback((id: string) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleToggleAll = useCallback((items: TransferListItem[]) => {
        setCheckedIds((prev) => {
            const allChecked = items.every((item) => prev.has(item.id));
            const next = new Set(prev);
            if (allChecked) {
                items.forEach((item) => next.delete(item.id));
            } else {
                items.forEach((item) => next.add(item.id));
            }
            return next;
        });
    }, []);

    const handleMoveRight = useCallback(() => {
        const leftCheckedSet = new Set(
            left.filter((item) => checkedIds.has(item.id)).map((i) => i.id),
        );
        const leftCheckedItems = left.filter((item) =>
            leftCheckedSet.has(item.id),
        );
        onChange(not(left, leftCheckedSet), [...right, ...leftCheckedItems]);
        setCheckedIds((prev) => {
            const next = new Set(prev);
            leftCheckedSet.forEach((id) => next.delete(id));
            return next;
        });
    }, [left, right, checkedIds, onChange]);

    const handleMoveLeft = useCallback(() => {
        const rightCheckedSet = new Set(
            right.filter((item) => checkedIds.has(item.id)).map((i) => i.id),
        );
        const rightCheckedItems = right.filter((item) =>
            rightCheckedSet.has(item.id),
        );
        onChange([...left, ...rightCheckedItems], not(right, rightCheckedSet));
        setCheckedIds((prev) => {
            const next = new Set(prev);
            rightCheckedSet.forEach((id) => next.delete(id));
            return next;
        });
    }, [left, right, checkedIds, onChange]);

    const handleMoveAllRight = useCallback(() => {
        onChange([], [...right, ...left]);
        setCheckedIds((prev) => {
            const next = new Set(prev);
            left.forEach((item) => next.delete(item.id));
            return next;
        });
    }, [left, right, onChange]);

    const handleMoveAllLeft = useCallback(() => {
        onChange([...left, ...right], []);
        setCheckedIds((prev) => {
            const next = new Set(prev);
            right.forEach((item) => next.delete(item.id));
            return next;
        });
    }, [left, right, onChange]);

    const customList = (title: string, items: TransferListItem[]) => {
        const numberOfChecked = items.filter((item) =>
            checkedIds.has(item.id),
        ).length;

        return (
            <Card>
                <CardHeader
                    sx={{ px: 2, py: 1 }}
                    avatar={
                        <Checkbox
                            onClick={() => handleToggleAll(items)}
                            checked={
                                numberOfChecked === items.length &&
                                items.length !== 0
                            }
                            indeterminate={
                                numberOfChecked !== items.length &&
                                numberOfChecked !== 0
                            }
                            disabled={items.length === 0 || isLoading}
                            aria-label="select all items"
                        />
                    }
                    title={title}
                    subheader={`${numberOfChecked}/${items.length} selected`}
                />
                <Divider />
                <Box sx={{ position: 'relative' }}>
                    <List
                        sx={{
                            minWidth: 250,
                            height: 300,
                            overflow: 'auto',
                        }}
                        dense
                        role="list"
                    >
                        {items.map((item) => (
                            <TransferListRow
                                key={item.id}
                                item={item}
                                checked={checkedIds.has(item.id)}
                                disabled={isLoading}
                                onToggle={handleToggle}
                            />
                        ))}
                    </List>
                    {isLoading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            }}
                        >
                            <CircularProgress size={32} />
                        </Box>
                    )}
                </Box>
            </Card>
        );
    };

    return (
        <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
            <TextField
                size="small"
                placeholder="Search…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Icon name="search" />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => setSearchQuery('')}
                                    aria-label="clear search"
                                >
                                    <Icon name="close" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    },
                }}
                sx={{ minWidth: 250 }}
            />
            <Grid
                container
                spacing={2}
                sx={{ justifyContent: 'center', alignItems: 'center' }}
            >
                <Grid>{customList(leftTitle, filteredLeft)}</Grid>
                <Grid>
                    <Stack direction="column" sx={{ alignItems: 'center' }}>
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleMoveAllRight}
                            disabled={left.length === 0 || isLoading}
                            aria-label="move all right"
                        >
                            <Icon name="keyboard_double_arrow_right" />
                        </Button>
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleMoveRight}
                            disabled={leftChecked.length === 0 || isLoading}
                            aria-label="move selected right"
                        >
                            <Icon name="chevron_right" />
                        </Button>
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleMoveLeft}
                            disabled={rightChecked.length === 0 || isLoading}
                            aria-label="move selected left"
                        >
                            <Icon name="chevron_left" />
                        </Button>
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleMoveAllLeft}
                            disabled={right.length === 0 || isLoading}
                            aria-label="move all left"
                        >
                            <Icon name="keyboard_double_arrow_left" />
                        </Button>
                    </Stack>
                </Grid>
                <Grid>{customList(rightTitle, filteredRight)}</Grid>
            </Grid>
        </Stack>
    );
};
